// File: src/components/Layout/FuturisticSidebar.jsx

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { motion } from "framer-motion";

import {
  ArrowRight,
  BarChart3,
  Building2,
  Calendar,
  ClipboardList,
  FileText,
  Home,
  LogOut,
  Package,
  ReceiptText,
  Shield,
  ShoppingCart,
  Truck,
  XCircle,
} from "lucide-react";

import {
  Link,
  useLocation,
} from "react-router-dom";

import { useUserPermissions } from "@/hooks/useUserPermissions";
import { useAuth } from "@/context/AuthProvider";

import api from "@/lib/axios";
import { notifyOperationalContextChanged } from "@/lib/procurementOperationalContext";

/* ──────────────────────────────────────────────────────────────
 * Helpers
 * ────────────────────────────────────────────────────────────── */

function isPlatformAdminDenied(error) {
  const status =
    error?.response?.status;

  if (status !== 403) {
    return false;
  }

  const data =
    error?.response?.data ||
    {};

  const code =
    String(
      data?.error ||
        data?.code ||
        data?.message ||
        ""
    )
      .trim()
      .toUpperCase();

  return (
    code ===
      "PLATFORM_ADMIN_REQUIRED" ||
    code.includes(
      "PLATFORM_ADMIN_REQUIRED"
    )
  );
}

function normalizeAdminContext(raw) {
  if (
    !raw ||
    typeof raw !== "object"
  ) {
    return null;
  }

  const companyId =
    raw.company_id != null
      ? Number(
          raw.company_id
        )
      : null;

  const unitId =
    raw.unit_id != null
      ? Number(
          raw.unit_id
        )
      : null;

  const unitSlug =
    raw.unit_slug
      ? String(
          raw.unit_slug
        )
      : "";

  const companyName =
    raw.company_name
      ? String(
          raw.company_name
        )
      : "";

  const unitName =
    raw.unit_name
      ? String(
          raw.unit_name
        )
      : "";

  if (
    !Number.isInteger(
      companyId
    ) ||
    companyId <= 0 ||
    !Number.isInteger(
      unitId
    ) ||
    unitId <= 0
  ) {
    return null;
  }

  return {
    ...raw,

    company_id:
      companyId,

    unit_id:
      unitId,

    unit_slug:
      unitSlug,

    company_name:
      companyName,

    unit_name:
      unitName,
  };
}

/* ──────────────────────────────────────────────────────────────
 * Component
 * ────────────────────────────────────────────────────────────── */

export default function FuturisticSidebar() {
  const location =
    useLocation();

  const {
    user,
    logout,
  } =
    useAuth();

  const permissions =
    useUserPermissions();

  const {
    canAccessProcurement,
    canAccessInventory,

    isAdmin,
    isDeveloper,
    isFinance,
    isProcurement,
    isManager,
    isPlatformAdmin,

    hasRole,
    hasPermission,
  } =
    permissions;

  /* ────────────────────────────────────────────────────────────
   * Navigation visibility
   *
   * Server-resolved effective permissions are preferred wherever
   * the backend permission catalogue already supports the module.
   *
   * Backend authorization remains authoritative.
   * ──────────────────────────────────────────────────────────── */

  const canSeeProcurement =
    Boolean(
      canAccessProcurement
    );

  const canSeePOApprovals =
    Boolean(
      hasPermission?.(
        "procurement.po.approve"
      )
    );

  const canSeeBusinessReports = ['procurement', 'inventory', 'operational', 'finance']
    .some(component => hasPermission?.(`report.${component}.view`));

  const canSeeReceiving =
    Boolean(
      hasPermission?.(
        "procurement.receiving.view"
      )
    );

  /*
   * `viewer` is the launch-facing Goods Receiving role.
   *
   * This only influences workspace/navigation presentation.
   * Permission checks remain authoritative.
   */
  const isGoodsReceiving =
    Boolean(
      hasRole?.(
        "viewer"
      )
    );

  const canSeeEvents =
    Boolean(
      hasPermission?.(
        "event.event.view"
      )
    );

  const canSeeInvoices =
    Boolean(
      hasPermission?.(
        "invoice.invoice.view"
      )
    );

  const canSeeInventory =
    Boolean(
      canAccessInventory
    );

  /*
   * Vendor visibility is still transitional.
   *
   * The current sidebar previously used these server-derived roles.
   * Keep that behavior until the vendor UI is fully migrated to
   * explicit vendor.vendor.view.
   */
  const canSeeVendors =
    Boolean(
      isAdmin ||
        isDeveloper ||
        isFinance ||
        isProcurement ||
        isManager
    );

  /*
   * Current server permission model has not yet been fully migrated
   * to granular leave permissions.
   *
   * If the backend already supplies explicit permission names,
   * these work immediately.
   *
   * We deliberately do not invent broader access here.
   */
  const canRequestLeave =
    Boolean(
      hasPermission?.(
        "leave.request"
      ) ||
        hasPermission?.(
          "leave.create"
        ) ||
        hasRole?.(
          "staffing"
        )
    );

  const canApproveLeave =
    Boolean(
      hasPermission?.(
        "leave.approve"
      ) ||
        hasRole?.([
          "admin",
          "developer",
          "manager",
          "staffing",
        ])
    );

  /* ────────────────────────────────────────────────────────────
   * Platform-admin context
   *
   * SECURITY:
   *
   * There is exactly one authority:
   *
   *   GET /admin/context
   *
   * The signed HttpOnly admin_ctx cookie is validated by the server.
   *
   * There is NO:
   *
   * - localStorage fallback
   * - browser context mirror
   * - X-Admin-* header
   * - JWT-derived tenant override
   * ──────────────────────────────────────────────────────────── */

  const [
    adminContext,
    setAdminContext,
  ] =
    useState(null);

  const [
    contextLoading,
    setContextLoading,
  ] =
    useState(false);

  const [
    contextError,
    setContextError,
  ] =
    useState("");

  const aliveRef =
    useRef(true);

  const requestIdRef =
    useRef(0);

  useEffect(() => {
    aliveRef.current =
      true;

    return () => {
      aliveRef.current =
        false;
    };
  }, []);

  const dispatchPlatformDenied =
    useCallback(() => {
      try {
        window.dispatchEvent(
          new CustomEvent(
            "auth:platform-admin-denied"
          )
        );

        window.dispatchEvent(
          new CustomEvent(
            "auth:go-dashboard",
            {
              detail: {
                reason:
                  "platform-admin-denied",
              },
            }
          )
        );
      } catch {
        // noop
      }
    }, []);

  const refreshAdminContext =
    useCallback(
      async ({
        quiet = false,
      } = {}) => {
        /*
         * /auth/me is the authority for whether this user may even
         * request platform-admin context.
         */
        if (!isPlatformAdmin) {
          if (
            aliveRef.current
          ) {
            setAdminContext(
              null
            );

            setContextError(
              ""
            );
          }

          return {
            ok: false,
          };
        }

        const requestId =
          ++requestIdRef.current;

        if (
          !quiet &&
          aliveRef.current
        ) {
          setContextLoading(
            true
          );
        }

        if (
          aliveRef.current
        ) {
          setContextError(
            ""
          );
        }

        try {
          const {
            data,
          } =
            await api.get(
              "/admin/context"
            );

          if (
            !aliveRef.current ||
            requestId !==
              requestIdRef.current
          ) {
            return {
              ok: false,
            };
          }

          /*
           * Server null means null.
           *
           * Never restore browser-side context.
           */
          const context =
            normalizeAdminContext(
              data?.context
            );

          setAdminContext(
            context
          );

          return {
            ok: true,
            context,
          };
        } catch (error) {
          if (
            !aliveRef.current ||
            requestId !==
              requestIdRef.current
          ) {
            return {
              ok: false,
            };
          }

          setAdminContext(
            null
          );

          if (
            isPlatformAdminDenied(
              error
            )
          ) {
            setContextError(
              "Platform access unavailable."
            );

            dispatchPlatformDenied();

            return {
              ok: false,
              denied: true,
            };
          }

          /*
           * Unknown/network failure must remain unknown.
           *
           * Do not replace it with stale localStorage data.
           */
          setContextError(
            "Context unavailable."
          );

          return {
            ok: false,
            error,
          };
        } finally {
          if (
            aliveRef.current &&
            requestId ===
              requestIdRef.current &&
            !quiet
          ) {
            setContextLoading(
              false
            );
          }
        }
      },
      [
        isPlatformAdmin,
        dispatchPlatformDenied,
      ]
    );

  useEffect(() => {
    /*
     * Invalidate any pending context response when identity changes.
     */
    requestIdRef.current +=
      1;

    if (!isPlatformAdmin) {
      setAdminContext(
        null
      );

      setContextError(
        ""
      );

      setContextLoading(
        false
      );

      return;
    }

    refreshAdminContext();
  }, [
    isPlatformAdmin,
    user?.id,
    refreshAdminContext,
  ]);

  const clearAdminContext =
    useCallback(
      async () => {
        if (
          !isPlatformAdmin ||
          contextLoading
        ) {
          return;
        }

        setContextLoading(
          true
        );

        setContextError(
          ""
        );

        /*
         * Invalidate older GET requests before changing context.
         */
        requestIdRef.current +=
          1;

        try {
          await api.delete(
            "/admin/context"
          );
          notifyOperationalContextChanged();

          if (
            aliveRef.current
          ) {
            setAdminContext(
              null
            );
          }
        } catch (error) {
          if (
            !aliveRef.current
          ) {
            return;
          }

          if (
            isPlatformAdminDenied(
              error
            )
          ) {
            setAdminContext(
              null
            );

            setContextError(
              "Platform access unavailable."
            );

            dispatchPlatformDenied();

            return;
          }

          /*
           * Do not pretend the context was cleared when the authoritative
           * DELETE failed.
           *
           * Refresh from the server to determine the real state.
           */
          setContextError(
            "Unable to clear context."
          );

          await refreshAdminContext({
            quiet: true,
          });
        } finally {
          if (
            aliveRef.current
          ) {
            setContextLoading(
              false
            );
          }
        }
      },
      [
        isPlatformAdmin,
        contextLoading,
        dispatchPlatformDenied,
        refreshAdminContext,
      ]
    );

  /* ────────────────────────────────────────────────────────────
   * Context label
   * ──────────────────────────────────────────────────────────── */

  const contextLabel =
    useMemo(() => {
      if (!adminContext) {
        return "Not set";
      }

      const company =
        adminContext.company_name ||
        (
          adminContext.company_id
            ? `Company ${adminContext.company_id}`
            : ""
        );

      const unit =
        adminContext.unit_name ||
        adminContext.unit_slug ||
        (
          adminContext.unit_id
            ? `Unit ${adminContext.unit_id}`
            : ""
        );

      if (
        company &&
        unit
      ) {
        return `${company} • ${unit}`;
      }

      return (
        company ||
        unit ||
        "Not set"
      );
    }, [
      adminContext,
    ]);

  /* ────────────────────────────────────────────────────────────
   * Navigation definitions
   * ──────────────────────────────────────────────────────────── */

  const platformAdminItems =
    useMemo(
      () =>
        isPlatformAdmin
          ? [
              {
                name:
                  "Platform Admin",

                icon:
                  <Shield
                    size={18}
                  />,

                path:
                  "/admin",
              },

              {
                name:
                  "Companies & Onboarding",

                icon:
                  <Building2
                    size={18}
                  />,

                path:
                  "/admin/companies",
              },
            ]
          : [],
      [
        isPlatformAdmin,
      ]
    );

  /*
   * Primary navigation.
   *
   * Goods Receiving receives its focused operational workspace and
   * does not automatically receive the generic Dashboard entry.
   *
   * Events / Invoices only render when their server permissions exist.
   */
  const baseItems =
    useMemo(
      () => [
        ...(user?.role === 'company_director' ? [{
          name: 'Company dashboard', icon: <Building2 size={18} />, path: '/company',
        }] : []),
        ...(canSeeBusinessReports ? [{ name: 'Business reports', icon: <BarChart3 size={18} />,
          path: user?.role === 'company_director' ? '/company/reports' : '/reports/business' }] : []),
        ...(!isGoodsReceiving
          ? [
              {
                name:
                  "Dashboard",

                icon:
                  <Home
                    size={18}
                  />,

                path:
                  "/dashboard",
              },
            ]
          : []),

        ...(canSeeEvents
          ? [
              {
                name:
                  "Events",

                icon:
                  <Calendar
                    size={18}
                  />,

                path:
                  "/events",
              },
            ]
          : []),

        ...(canSeeInvoices
          ? [
              {
                name:
                  "Invoices",

                icon:
                  <ReceiptText
                    size={18}
                  />,

                path:
                  "/invoices",
              },
            ]
          : []),

        ...(canSeeProcurement
          ? [
              {
                name:
                  "Procurement",

                icon:
                  <ShoppingCart
                    size={18}
                  />,

                path:
                  "/procurement",
              },
            ]
          : []),

        ...(canSeeInventory
          ? [
              {
                name:
                  "Inventory",

                icon:
                  <Package
                    size={18}
                  />,

                path:
                  "/inventory",
              },
            ]
          : []),
      ],
      [
        user?.role,
        canSeeBusinessReports,
        isGoodsReceiving,
        canSeeEvents,
        canSeeInvoices,
        canSeeProcurement,
        canSeeInventory,
      ]
    );

  /*
   * Operational / secondary navigation.
   *
   * Receiving is intentionally independent of broad Procurement visibility.
   * A Goods Receiving user can therefore receive POs without being given the
   * main Procurement workspace.
   */
  const extraItems =
    useMemo(
      () => [
        ...(canSeeReceiving
          ? [
              {
                name:
                  "Receiving Dashboard",

                icon:
                  <Truck
                    size={18}
                  />,

                path:
                  "/po-receiving-dashboard",
              },

              {
                name:
                  "Receiving Activity",

                icon:
                  <Truck
                    size={18}
                  />,

                path:
                  "/receiving-activity",
              },
            ]
          : []),

        ...(canSeeProcurement
          ? [
              {
                name:
                  "PO Management",

                icon:
                  <ClipboardList
                    size={18}
                  />,

                path:
                  "/po-management",
              },

              ...(canSeePOApprovals
                ? [
                    {
                      name:
                        "PO Approvals",

                      icon:
                        <FileText
                          size={18}
                        />,

                      path:
                        "/procurement/po-approval",
                    },
                  ]
                : []),

              {
                name:
                  "Procurement Report",

                icon:
                  <BarChart3
                    size={18}
                  />,

                path:
                  "/procurement/report",
              },
            ]
          : []),

        ...(canSeeInventory
          ? [
              {
                name:
                  "Stock Items",

                icon:
                  <Package
                    size={18}
                  />,

                path:
                  "/inventory/stock-items",
              },

              {
                name:
                  "Stock Levels",

                icon:
                  <Package
                    size={18}
                  />,

                path:
                  "/inventory/stock-levels",
              },

              {
                name:
                  "Stock Movements",

                icon:
                  <Package
                    size={18}
                  />,

                path:
                  "/inventory/stock-movements",
              },

              {
                name:
                  "Bins & Locations",

                icon:
                  <Package
                    size={18}
                  />,

                path:
                  "/inventory/bins-locations",
              },

              {
                name:
                  "Menu Builder",

                icon:
                  <Package
                    size={18}
                  />,

                path:
                  "/inventory/menu-builder",
              },
            ]
          : []),

        ...(canSeeVendors
          ? [
              {
                name:
                  "Vendors",

                icon:
                  <Building2
                    size={18}
                  />,

                path:
                  "/vendors",
              },
            ]
          : []),

        ...(canRequestLeave
          ? [
              {
                name:
                  "Request Leave",

                icon:
                  <FileText
                    size={18}
                  />,

                path:
                  "/staff/leave-request",
              },
            ]
          : []),

        ...(canApproveLeave
          ? [
              {
                name:
                  "Leave Approvals",

                icon:
                  <FileText
                    size={18}
                  />,

                path:
                  "/staff/leave-approvals",
              },
            ]
          : []),
      ],
      [
        canSeeReceiving,
        canSeeProcurement,
        canSeePOApprovals,
        canSeeInventory,
        canSeeVendors,
        canRequestLeave,
        canApproveLeave,
      ]
    );

  /* ────────────────────────────────────────────────────────────
   * Merge + deduplicate
   * ──────────────────────────────────────────────────────────── */

  const navItems =
    useMemo(() => {
      const all = [
        ...platformAdminItems,
        ...baseItems,
        ...extraItems,
      ];

      const seen =
        new Set();

      return all.filter(
        (item) => {
          if (!item?.path) {
            return false;
          }

          if (
            seen.has(
              item.path
            )
          ) {
            return false;
          }

          seen.add(
            item.path
          );

          return true;
        }
      );
    }, [
      platformAdminItems,
      baseItems,
      extraItems,
    ]);

  /* ────────────────────────────────────────────────────────────
   * Active route
   * ──────────────────────────────────────────────────────────── */

  const isActive =
    useCallback(
      (path) =>
        location.pathname ===
          path ||
        location.pathname.startsWith(
          `${path}/`
        ),
      [
        location.pathname,
      ]
    );

  /* ────────────────────────────────────────────────────────────
   * Logout
   * ──────────────────────────────────────────────────────────── */

  const handleLogout =
    useCallback(
      async () => {
        if (
          typeof logout ===
          "function"
        ) {
          await logout();
        }
      },
      [
        logout,
      ]
    );

  /* ────────────────────────────────────────────────────────────
   * Render
   * ──────────────────────────────────────────────────────────── */

  return (
    <motion.aside
      initial={{
        x: -60,
        opacity: 0,
      }}
      animate={{
        x: 0,
        opacity: 1,
      }}
      transition={{
        duration: 0.6,
      }}
      className="fixed top-0 left-0 h-screen w-60 bg-gradient-to-br from-[#0f172a] to-black shadow-2xl z-50 border-r border-[#1e293b] text-sm flex flex-col"
    >
      {/* Header */}

      <div className="p-6 border-b border-[#334155] shrink-0">
        <h1 className="text-2xl font-extrabold tracking-wide bg-gradient-to-r from-cyan-400 to-purple-500 bg-clip-text text-transparent">
          SOLO.MDSH
        </h1>

        <p className="text-xs text-gray-400 mt-1">
          System Navigation
        </p>

        {/* Platform-admin context */}

        {isPlatformAdmin && (
          <div className="mt-4 rounded-xl border border-[#334155] bg-[#0b1220]/60 p-3">
            <div className="flex items-center justify-between gap-2">
              <div className="text-[11px] text-gray-400">
                Platform Context
              </div>

              <button
                type="button"
                onClick={() =>
                  refreshAdminContext()
                }
                disabled={
                  contextLoading
                }
                className="text-[11px] text-gray-300 hover:text-white underline underline-offset-2 disabled:opacity-60"
                title="Refresh context from server"
              >
                {contextLoading
                  ? "…"
                  : "Refresh"}
              </button>
            </div>

            <div className="mt-1 text-xs text-gray-200 leading-snug">
              {contextLabel}
            </div>

            {contextError && (
              <div className="mt-1 text-[10px] text-amber-300/90 leading-snug">
                {contextError}
              </div>
            )}

            <div className="mt-3 flex gap-2">
              <Link
                to="/admin/companies"
                className="flex-1 inline-flex items-center justify-center gap-1 rounded-lg bg-[#1e293b] hover:bg-[#243044] text-gray-200 px-2 py-1 text-[11px]"
                title="Set or change platform context"
              >
                Set

                <ArrowRight
                  size={14}
                />
              </Link>

              <button
                type="button"
                onClick={
                  clearAdminContext
                }
                disabled={
                  contextLoading ||
                  !adminContext
                }
                className="inline-flex items-center justify-center gap-1 rounded-lg border border-[#334155] hover:border-red-500 text-gray-200 px-2 py-1 text-[11px] disabled:opacity-40"
                title="Clear platform context"
              >
                <XCircle
                  size={14}
                />

                Clear
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Navigation */}

      <div className="flex-1 overflow-y-auto mt-4 px-4 pb-6">
        <div className="space-y-2">
          {navItems.map(
            (item) => {
              const active =
                isActive(
                  item.path
                );

              return (
                <Link
                  key={
                    item.path
                  }
                  to={
                    item.path
                  }
                >
                  <motion.div
                    whileHover={{
                      scale: 1.03,
                    }}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl cursor-pointer font-medium transition-all duration-200 ${
                      active
                        ? "bg-gradient-to-r from-blue-600 to-indigo-500 text-white shadow-md"
                        : "text-gray-300 hover:bg-[#1e293b]"
                    }`}
                  >
                    {item.icon}

                    <span className="tracking-wide">
                      {item.name}
                    </span>
                  </motion.div>
                </Link>
              );
            }
          )}
        </div>
      </div>

      {/* Footer */}

      <div className="shrink-0 px-6 py-4 border-t border-[#334155] bg-gradient-to-br from-[#0f172a] to-black">
        <button
          type="button"
          onClick={
            handleLogout
          }
          className="w-full flex items-center justify-center gap-2 py-2 px-4 text-gray-400 hover:text-white border border-transparent hover:border-red-500 transition-all rounded-xl"
        >
          <LogOut
            size={16}
          />

          Logout
        </button>
      </div>
    </motion.aside>
  );
}
